import { getKoala } from '#/vendors/koala';
import { getPosthog } from '#/vendors/posthog';

import type { AnalyticsCapture, AnalyticsEventData } from './types';
import { serializeEventName } from './utils';

/**
 * Get the analytics library, without relying on React hooks/components.
 *
 * @returns The analytics library.
 */
export function getAnalytics() {
  const posthog = getPosthog();
  const koala = getKoala();

  /*
  |------------------
  | Utils
  |------------------
  */

  /**
   * Collect the base event data.
   */
  const collectBaseEventData = () => {
    return {
      application: 'quarks',
      sessionId: posthog?.get_session_id(),
    };
  };

  /*
  |------------------
  | Overrides & Customizations
  |------------------
  */

  const capture: AnalyticsCapture = async (eventName, eventData) => {
    const collectedData = collectBaseEventData();
    const serializedEventName = serializeEventName(eventName);

    posthog?.capture(serializedEventName, {
      ...eventData,
      ...collectedData,
    });

    void koala?.track(serializedEventName, {
      ...eventData,
      ...collectedData,
    });
  };

  const captureException = (error: unknown, data: AnalyticsEventData) => {
    const err = error instanceof Error ? error : new Error('Unknown error');

    posthog?.captureException(err, {
      name: err.name,
      message: err.message,
      stack: err.stack,
      ...data,
    });

    koala?.trackError(err);
  };

  const identify = (payload: { email: string }) => {
    posthog?.identify(undefined, {
      email: payload.email,
    });
    void koala?.identify(payload.email);
  };

  return {
    capture,
    captureException,
    identify,
  };
}
